// Chip token format: `[[sv:<smartViewId>]]` (smart-view) or `[[note:<noteId>]]`.
// Tokens are embedded inline in any text-bearing block field — markdown body,
// keyValue value, table cell, task label. They survive round-trips through
// the agent and storage as opaque strings.
//
// Why not include the title in the token? Because titles change. Resolving at
// render time via Convex means a rename on the target propagates everywhere
// without rewriting block content. Same trick lets dedupe/redirect Just Work.

export type ChipKind = "smart-view" | "note";

export interface ChipToken {
  kind: ChipKind;
  id: string;
}

export type ChipSegment = { type: "text"; value: string } | { type: "chip"; chip: ChipToken };

const CHIP_PATTERN = /\[\[(sv|note):([A-Za-z0-9_\-:.]+)\]\]/g;

const kindFromPrefix = (prefix: string): ChipKind | null => {
  switch (prefix) {
    case "sv":
      return "smart-view";
    case "note":
      return "note";
    default:
      return null;
  }
};

export const parseChips = (text: string): ChipSegment[] => {
  if (text.length === 0) return [];

  const segments: ChipSegment[] = [];
  let cursor = 0;

  // Reset lastIndex since we use the global flag and the regex is module-level.
  CHIP_PATTERN.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = CHIP_PATTERN.exec(text)) !== null) {
    const [whole, prefix, id] = match;
    const kind = kindFromPrefix(prefix);
    if (kind === null) continue;

    if (match.index > cursor) {
      segments.push({ type: "text", value: text.slice(cursor, match.index) });
    }
    segments.push({ type: "chip", chip: { kind, id } });
    cursor = match.index + whole.length;
  }

  if (cursor < text.length) {
    segments.push({ type: "text", value: text.slice(cursor) });
  }

  return segments;
};

export const hasChips = (text: string): boolean => {
  CHIP_PATTERN.lastIndex = 0;
  return CHIP_PATTERN.test(text);
};

export const collectChipIds = (
  segments: ChipSegment[]
): { smartViewIds: string[]; noteIds: string[] } => {
  const smartViewIds = new Set<string>();
  const noteIds = new Set<string>();
  for (const segment of segments) {
    if (segment.type !== "chip") continue;
    if (segment.chip.kind === "smart-view") smartViewIds.add(segment.chip.id);
    else noteIds.add(segment.chip.id);
  }
  return { smartViewIds: [...smartViewIds], noteIds: [...noteIds] };
};

// URL scheme used when rewriting chips to markdown links for MemCommonMarkdown.
// onClickLink intercepts these and routes via the JIT/navigation flow.
export const CHIP_LINK_SCHEME = "mem-chip:";

export const buildChipHref = (chip: ChipToken): string => {
  const path = chip.kind === "smart-view" ? "sv" : "note";
  return `${CHIP_LINK_SCHEME}//${path}/${chip.id}`;
};

export const parseChipHref = (href: string): ChipToken | null => {
  if (!href.startsWith(`${CHIP_LINK_SCHEME}//`)) return null;
  const rest = href.slice(`${CHIP_LINK_SCHEME}//`.length);
  const slash = rest.indexOf("/");
  if (slash === -1) return null;
  const path = rest.slice(0, slash);
  const id = rest.slice(slash + 1);
  if (id.length === 0) return null;
  if (path === "sv") return { kind: "smart-view", id };
  if (path === "note") return { kind: "note", id };
  return null;
};

// Replace chip tokens with markdown links suitable for MemCommonMarkdown.
// `resolveTitle` is called per chip — return `undefined` if the title isn't
// resolved yet and a placeholder will be used.
export const renderChipsAsMarkdownLinks = (
  text: string,
  resolveTitle: (chip: ChipToken) => string | undefined
): string => {
  if (!hasChips(text)) return text;
  return parseChips(text)
    .map(segment => {
      if (segment.type === "text") return segment.value;
      const title = resolveTitle(segment.chip) ?? defaultPlaceholder(segment.chip);
      const safeTitle = escapeMarkdownLinkText(title);
      return `[${safeTitle}](${buildChipHref(segment.chip)})`;
    })
    .join("");
};

const defaultPlaceholder = (chip: ChipToken): string =>
  chip.kind === "smart-view" ? "Smart view" : "Note";

const escapeMarkdownLinkText = (text: string): string => text.replace(/([[\]\\])/g, "\\$1");
